import React from 'react';
import { FileSpreadsheet, Table2, Upload } from 'lucide-react';

/**
 * Visual of CRM v2 spreadsheet import: file chosen + how rows map to records.
 * Sized to use full modal width and most of the available viewport height.
 */
export default function TutorialCrmUploadMockup() {
  const columns = [
    'Name',
    'Phone',
    'Email',
    'Company',
    'Title',
    'City',
    'State',
    'Status',
    'Source',
    'Last contact',
  ];
  const rows = [
    [
      'Jordan Lee',
      '+1 555-0101',
      'jordan@acmeco.com',
      'Acme Co.',
      'VP Operations',
      'Austin',
      'TX',
      'New lead',
      'Web form',
      '2025-03-12',
    ],
    [
      'Sam Rivera',
      '+1 555-0102',
      'sam@brightline.io',
      'Brightline',
      'Director',
      'Denver',
      'CO',
      'Follow-up',
      'Referral',
      '2025-03-10',
    ],
    [
      'Priya Shah',
      '+1 555-0103',
      'priya@northwind.org',
      'Northwind',
      'IT Manager',
      'Seattle',
      'WA',
      'Qualified',
      'Cold outreach',
      '2025-03-08',
    ],
    [
      'Marcus Chen',
      '+1 555-0104',
      'marcus@example.net',
      'Example Net',
      'Owner',
      'Miami',
      'FL',
      'New lead',
      'Event',
      '2025-03-14',
    ],
  ];

  return (
    <div
      className="mt-5 -mx-6 sm:-mx-8 flex flex-col min-h-[min(78vh,820px)]"
      aria-hidden
    >
      <div className="px-6 sm:px-8 shrink-0">
        <p className="text-sm font-semibold text-[#459EA3] uppercase tracking-wide mb-2">CRM import</p>
        <p className="text-sm text-gray-500 mb-4">
          In <strong className="text-gray-700">Insights → CRM</strong>, use <strong className="text-gray-700">Import data</strong> with a{' '}
          <strong className="text-gray-700">.csv, .xls, or .xlsx</strong> file. The{' '}
          <strong className="text-gray-700">first row</strong> becomes your field names; each row below becomes one contact record.
        </p>
      </div>

      <div className="flex-1 min-h-0 flex flex-col px-6 sm:px-8 pb-1">
        <div className="flex-1 min-h-0 flex flex-col rounded-2xl border border-slate-200 bg-white shadow-md overflow-hidden w-full">
          <div className="shrink-0 flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 px-4 py-3 sm:px-6 sm:py-4 bg-slate-50/80">
            <div className="min-w-0">
              <h3 className="text-base sm:text-lg font-semibold text-slate-900">Import data</h3>
              <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
                Upload a spreadsheet, review the parse, then import into a new or existing group.
              </p>
            </div>
            <span className="inline-flex items-center rounded-full border border-teal-200 bg-teal-50 px-3 py-1 text-xs font-semibold text-teal-800 shrink-0">
              New group
            </span>
          </div>

          <div className="flex-1 min-h-0 flex flex-col p-4 sm:p-6 gap-4 sm:gap-5">
            <div className="shrink-0">
              <span className="text-xs sm:text-sm font-medium text-slate-700">Data file</span>
              <div className="mt-2 flex flex-col items-center justify-center rounded-3xl border-2 border-dashed border-teal-300/80 bg-teal-50/60 px-4 py-5 sm:py-7 min-h-[min(16vh,160px)] text-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white shadow-sm border border-slate-200 mb-3">
                  <FileSpreadsheet className="h-7 w-7 text-teal-600" />
                </div>
                <p className="text-sm sm:text-base font-semibold text-slate-800">march_prospects.csv</p>
                <p className="mt-1 flex flex-wrap items-center justify-center gap-1.5 text-xs sm:text-sm text-slate-500 px-2">
                  <Upload className="h-3.5 w-3.5 shrink-0" />
                  First row = column headers · each following row = one record
                </p>
              </div>
            </div>

            <div className="flex-1 min-h-0 flex flex-col min-h-[min(38vh,360px)]">
              <div className="flex items-center gap-2 mb-2 shrink-0">
                <Table2 className="h-4 w-4 sm:h-5 sm:w-5 text-slate-500 shrink-0" />
                <span className="text-xs sm:text-sm font-semibold text-slate-700">Preview (first rows)</span>
              </div>
              <div className="flex-1 min-h-0 rounded-xl border border-slate-200 overflow-hidden shadow-sm flex flex-col">
                <div className="flex-1 min-h-0 overflow-auto">
                  <table className="w-full text-left text-[10px] sm:text-xs border-collapse min-w-[900px]">
                    <thead className="sticky top-0 z-[1]">
                      <tr className="bg-slate-800 text-white">
                        {columns.map((col) => (
                          <th
                            key={col}
                            className="px-2 py-2 sm:px-3 sm:py-2.5 font-semibold whitespace-nowrap border-r border-slate-600/80 last:border-r-0"
                          >
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white text-slate-700">
                      {rows.map((cells, ri) => (
                        <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-slate-50/90'}>
                          {cells.map((cell, ci) => (
                            <td
                              key={ci}
                              className="px-2 py-2 sm:px-3 sm:py-2.5 border-t border-slate-200 border-r border-slate-100 last:border-r-0 whitespace-nowrap"
                            >
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <p className="text-xs sm:text-sm text-slate-500 mt-2 leading-relaxed shrink-0">
                After preview, you confirm import — then leads appear in that CRM group for calling, SMS, and reporting.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
